/**
 * What the DEVICE managed, as opposed to what the student answered.
 *
 * A sibling argument to the answer, never a field on it. `CompassAccuracy` established that shape
 * deliberately and its own doc named the alternative as a mistake already made — GPS nesting
 * `accuracy` inside `GPSAnswer` — so this generalises the sibling and retires the nesting rather
 * than repeating it a third time for Angle.
 *
 * The reason it has to exist at all: an evaluator that receives only a value and `touched` cannot
 * tell "the student did not answer" from "the student could not answer". Those are different
 * situations with different remedies and different fairness, and until the health travels beside
 * the answer no amount of feedback authoring can separate them.
 *
 * Every field is optional. A block that cannot report a figure omits it, and a caller with nothing
 * to say passes `{}` — which is exactly a healthy sensor, so the ordinary path needs no ceremony.
 */
/** A device with nothing to report and nothing wrong — the ordinary case, named so a call site
 *  reads as a deliberate "healthy" rather than an empty object someone forgot to fill. */
export const HEALTHY_SENSOR = {};
/** The trigger every sensor block names its no-data card by. One string, three blocks. */
export const NO_SENSOR_DATA_TRIGGER = 'no_sensor_data';
/**
 * The device failed AND the student had no way round it.
 *
 * The bypass is what decides fault, and it is the whole reason this is a function rather than a
 * field. With no manual route open, a dead sensor is the system's failure and belongs on its own
 * card; with one open and untaken, the student had a route and declined it, which is an ordinary
 * No Answer and should read as one.
 *
 * "Open" is asked at evaluation time, not read off the block, because `unlockBypass` is a Flow
 * EFFECT: a first attempt can have no bypass and the second one can, which is exactly the loop the
 * no-data card exists to start.
 */
export function isNoSensorData(health) {
    return health.fault === 'noData' && !health.bypassAvailable;
}
//# sourceMappingURL=sensor-health.js.map